import {
    CheckCircle,
    Error,
    ExpandMore as ExpandMoreIcon,
    InfoOutlined,
} from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import { addDays, format, parseISO } from 'date-fns';
import {
    default as React,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { theme } from '../../../../shared/theme';
import { SpaceType } from '../../../enums/experience';
import CustomTooltip from '../../../shared/CustomTooltip';
import SpaceCardContent from '../../../shared/SpaceCardContent';
import { BaseSpace } from '../../../types/experience';
import { ReservationSpace } from '../../../types/reservation';
import { reservationEditFieldLabelSx } from '../EditReservation/reservationEditFieldStyles';

export interface Space extends BaseSpace {
    id: number;
}

const isFreeBedroom = (space: Space) =>
    space.spaceType === SpaceType.Bedroom && space?.cleaningFee === 0;

type Props = {
    name: string;
    spaces: Space[];
    bookingStartDate: string;
    bookingEndDate: string;
    bookingCheckinTime: string;
    bookingCheckoutTime: string;
    experienceDetails: any;
    spaceType: string;
    isMeetingRoom?: boolean;
    handleError: (field: string, message: string) => void;
    clearError: (field: string) => void;
    hasByZoneCleaningFee: boolean;
    /** Request form: collapsed accordion; edit flow keeps inline layout. */
    collapsible?: boolean;
};

type DateTimeInterval = [string, string];

const toDateTime = (date: string, time: string) => `${date}T${time}`;

const splitDateTime = (dt: string): { date: string; time: string } => {
    const [date, time = '00:00'] = dt.split('T');
    return { date, time };
};

const compareDateTime = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

const clampInterval = (
    start: string,
    end: string,
    windowStart: string,
    windowEnd: string,
): DateTimeInterval | null => {
    const clampedStart = start < windowStart ? windowStart : start;
    const clampedEnd = end > windowEnd ? windowEnd : end;
    return clampedStart < clampedEnd ? [clampedStart, clampedEnd] : null;
};

type AllocationSegment = {
    id: number;
    startDT: string;
    endDT: string;
};

const allocationsToSegments = (
    allocations: ReservationSpace[],
): AllocationSegment[] =>
    allocations.map((alloc) => ({
        id: alloc.id,
        startDT: toDateTime(alloc.startDate, alloc.checkinTime),
        endDT: toDateTime(alloc.endDate, alloc.checkoutTime),
    }));

export type AllocationGroup = {
    startDT: string;
    endDT: string;
    officeId?: number;
    boardroomId?: number;
};

const buildAllocationGroups = (
    windowStartDT: string,
    windowEndDT: string,
    officeSegments: AllocationSegment[],
    boardroomSegments: AllocationSegment[],
    excludedIntervals: DateTimeInterval[] = [],
): AllocationGroup[] => {
    if (windowStartDT >= windowEndDT) return [];

    const breakpoints = new Set<string>([windowStartDT, windowEndDT]);
    for (const segment of [...officeSegments, ...boardroomSegments]) {
        if (segment.startDT > windowStartDT && segment.startDT < windowEndDT) {
            breakpoints.add(segment.startDT);
        }
        if (segment.endDT > windowStartDT && segment.endDT < windowEndDT) {
            breakpoints.add(segment.endDT);
        }
    }
    for (const [excludedStart, excludedEnd] of excludedIntervals) {
        if (excludedStart > windowStartDT && excludedStart < windowEndDT) {
            breakpoints.add(excludedStart);
        }
        if (excludedEnd > windowStartDT && excludedEnd < windowEndDT) {
            breakpoints.add(excludedEnd);
        }
    }
    const sorted = Array.from(breakpoints).sort(compareDateTime);

    const raw: AllocationGroup[] = [];
    for (let i = 0; i < sorted.length - 1; i++) {
        const start = sorted[i];
        const end = sorted[i + 1];
        if (start >= end) continue;

        const isExcluded = excludedIntervals.some(
            ([excludedStart, excludedEnd]) =>
                excludedStart <= start && excludedEnd >= end,
        );
        if (isExcluded) continue;

        const office = officeSegments.find(
            (s) => s.startDT <= start && s.endDT >= end,
        );
        const boardroom = boardroomSegments.find(
            (s) => s.startDT <= start && s.endDT >= end,
        );

        raw.push({
            startDT: start,
            endDT: end,
            officeId: office?.id,
            boardroomId: boardroom?.id,
        });
    }

    const merged: AllocationGroup[] = [];
    for (const segment of raw) {
        const last = merged[merged.length - 1];
        if (
            last &&
            last.endDT === segment.startDT &&
            last.officeId === segment.officeId &&
            last.boardroomId === segment.boardroomId
        ) {
            last.endDT = segment.endDT;
        } else {
            merged.push({ ...segment });
        }
    }
    return merged;
};

const formatGroupRange = (group: AllocationGroup): string => {
    const startSplit = splitDateTime(group.startDT);
    const endSplit = splitDateTime(group.endDT);

    const startDateString = new Date(startSplit.date).toDateString();
    const endDateString = new Date(endSplit.date).toDateString();

    if (startSplit.date === endSplit.date) {
        return `${startDateString} ${startSplit.time} – ${endSplit.time}`;
    }
    return `${startDateString} ${startSplit.time} – ${endDateString} ${endSplit.time}`;
};

const mergeIntervals = (intervals: DateTimeInterval[]): DateTimeInterval[] => {
    if (!intervals?.length) return [];
    const sorted = [...intervals]
        .filter(([s, e]) => s < e)
        .sort(([a], [b]) => compareDateTime(a, b));

    if (!sorted.length) return [];

    const merged: DateTimeInterval[] = [];
    let [currentStart, currentEnd] = sorted[0];

    for (let index = 1; index < sorted.length; index++) {
        const [nextStart, nextEnd] = sorted[index];
        if (nextStart <= currentEnd) {
            if (nextEnd > currentEnd) currentEnd = nextEnd;
        } else {
            merged.push([currentStart, currentEnd]);
            [currentStart, currentEnd] = [nextStart, nextEnd];
        }
    }
    merged.push([currentStart, currentEnd]);
    return merged;
};

const computeRemainingGaps = (
    windowStart: string,
    windowEnd: string,
    coveredIntervals: DateTimeInterval[],
): DateTimeInterval[] => {
    const mergedCovered = mergeIntervals(coveredIntervals);
    const gaps: DateTimeInterval[] = [];
    let cursor = windowStart;

    for (const [coveredStart, coveredEnd] of mergedCovered) {
        if (coveredEnd <= cursor) continue;
        if (coveredStart > cursor) {
            const gapEnd = coveredStart < windowEnd ? coveredStart : windowEnd;
            if (cursor < gapEnd) gaps.push([cursor, gapEnd]);
        }
        if (coveredEnd > cursor) cursor = coveredEnd;
        if (cursor >= windowEnd) break;
    }

    if (cursor < windowEnd) gaps.push([cursor, windowEnd]);
    return gaps;
};

//Build the non-business intervals, so the component would not try to fill the gaps after experience checkout time for the office and meeting rooms
const buildNonBusinessIntervals = (
    windowStartDT: string,
    windowEndDT: string,
    experienceCheckinTime: string,
    experienceCheckoutTime: string,
): DateTimeInterval[] => {
    const { date: firstDate } = splitDateTime(windowStartDT);
    const { date: lastDate } = splitDateTime(windowEndDT);

    const intervals: DateTimeInterval[] = [];
    let cursor = parseISO(firstDate);
    const stop = parseISO(lastDate);

    while (cursor <= stop) {
        const dateString = format(cursor, 'yyyy-MM-dd');
        const nextDateString = format(addDays(cursor, 1), 'yyyy-MM-dd');
        const overnightStart = toDateTime(dateString, experienceCheckoutTime);
        const overnightEnd = toDateTime(nextDateString, experienceCheckinTime);
        const clamped = clampInterval(
            overnightStart,
            overnightEnd,
            windowStartDT,
            windowEndDT,
        );
        if (clamped) intervals.push(clamped);
        cursor = addDays(cursor, 1);
    }
    return intervals;
};

const intersectIntervalLists = (
    listA: DateTimeInterval[],
    listB: DateTimeInterval[],
): DateTimeInterval[] => {
    const a = mergeIntervals(listA);
    const b = mergeIntervals(listB);
    let i = 0;
    let j = 0;
    const out: DateTimeInterval[] = [];

    while (i < a.length && j < b.length) {
        const [aStart, aEnd] = a[i];
        const [bStart, bEnd] = b[j];
        const start = aStart > bStart ? aStart : bStart;
        const end = aEnd < bEnd ? aEnd : bEnd;
        if (start < end) out.push([start, end]);
        if (aEnd < bEnd) i++;
        else j++;
    }
    return out;
};

const getSpaceAvailableIntervals = (
    space: Space,
    windowStartDT: string,
    windowEndDT: string,
): DateTimeInterval[] => {
    if (windowStartDT >= windowEndDT) return [];

    let available: DateTimeInterval[] = [[windowStartDT, windowEndDT]];

    const unavailableBlocks = (space.unavailablePeriods ?? [])
        .map((block) => {
            const blockStart = toDateTime(
                block.from,
                block.startTime ?? '00:00',
            );
            const blockEnd = toDateTime(block.to, block.endTime ?? '23:59');
            return clampInterval(
                blockStart,
                blockEnd,
                windowStartDT,
                windowEndDT,
            );
        })
        .filter(Boolean) as DateTimeInterval[];

    if (!unavailableBlocks.length) return available;

    unavailableBlocks.sort(([a], [b]) => compareDateTime(a, b));
    for (const [blockStart, blockEnd] of unavailableBlocks) {
        const nextAvailable: DateTimeInterval[] = [];
        for (const [availableStart, availableEnd] of available) {
            if (blockEnd <= availableStart || blockStart >= availableEnd) {
                nextAvailable.push([availableStart, availableEnd]);
            } else {
                if (availableStart < blockStart)
                    nextAvailable.push([availableStart, blockStart]);
                if (blockEnd < availableEnd)
                    nextAvailable.push([blockEnd, availableEnd]);
            }
        }
        available = nextAvailable;
        if (!available.length) break;
    }
    return mergeIntervals(available);
};

const getSpaceType = (space: any) => space?.spaceType ?? space?.type ?? '';
const isBoardroom = (space: any) => getSpaceType(space) === 'boardroom';
const isPrivateOffice = (space: any) => getSpaceType(space) === 'privateOffice';

const ReservationSpaces: React.FC<Props> = ({
    hasByZoneCleaningFee,
    name,
    spaces,
    bookingStartDate,
    bookingEndDate,
    bookingCheckinTime,
    bookingCheckoutTime,
    experienceDetails,
    spaceType,
    isMeetingRoom = false,
    handleError,
    clearError,
    collapsible = false,
}) => {
    const { control, setValue, getValues, watch } = useFormContext();
    const [roomAllocationExpanded, setRoomAllocationExpanded] = useState(false);
    const experienceType = experienceDetails?.type;

    const experienceCheckinTime = experienceDetails?.checkinTime;
    const experienceCheckoutTime = experienceDetails?.checkoutTime;

    const selectedSpaces = getValues('spaces').map((space: any) => ({
        ...space,
        type: space.type,
    }));

    const windowStartDT = useMemo(
        () => toDateTime(bookingStartDate, bookingCheckinTime),
        [bookingStartDate, bookingCheckinTime],
    );
    const windowEndDT = useMemo(
        () => toDateTime(bookingEndDate, bookingCheckoutTime),
        [bookingEndDate, bookingCheckoutTime],
    );

    useEffect(() => {
        const currentValue = getValues(name);
        if (!Array.isArray(currentValue))
            setValue(name, [], { shouldDirty: false });
    }, [name, setValue]);

    const selectedAllocations: ReservationSpace[] = watch(name) ?? [];
    const formSpaces: ReservationSpace[] = useWatch({
        control,
        name: 'spaces',
        defaultValue: [],
    });

    const isEntireVenue = spaceType === 'entireVenue';
    const isCommercial =
        String(experienceType ?? '').toLowerCase() === 'commercial';
    const isNonCommercial = experienceType !== 'commercial';
    const isDualTrack =
        isCommercial && spaceType === 'privateOffice' && isMeetingRoom === true;

    const showAllTogether = isEntireVenue || isNonCommercial;
    const showCommercialSpaceRequiredError =
        isCommercial && selectedAllocations.length < 1;

    const deskAndMeetingRoom = spaceType === 'desk' && isMeetingRoom;
    const showPrivateOfficeOnly =
        !isEntireVenue &&
        isCommercial &&
        !isDualTrack &&
        isCommercial &&
        spaceType === 'privateOffice';

    const showBoardroomOnly =
        !isEntireVenue &&
        isCommercial &&
        !isDualTrack &&
        (spaceType === 'meetingRoom' || deskAndMeetingRoom);

    const availableSpaces = useMemo(
        () =>
            spaces
                ?.sort((a, b) => {
                    if (a.spaceType === SpaceType.Bedroom) return -1;
                    if (b.spaceType === SpaceType.Bedroom) return 1;
                    return 0;
                })
                .filter(
                    (space) =>
                        getSpaceAvailableIntervals(
                            space,
                            windowStartDT,
                            windowEndDT,
                        ).length > 0,
                ),
        [
            spaces,
            bookingStartDate,
            bookingEndDate,
            bookingCheckinTime,
            bookingCheckoutTime,
        ],
    );

    const privateOfficeSpaces = useMemo(
        () => availableSpaces?.filter(isPrivateOffice),
        [availableSpaces],
    );
    const boardroomSpaces = useMemo(
        () => availableSpaces?.filter(isBoardroom),
        [availableSpaces],
    );
    const otherSpaces = useMemo(
        () =>
            availableSpaces?.filter(
                (s) => !isPrivateOffice(s) && !isBoardroom(s),
            ),
        [availableSpaces],
    );

    const selectedOfficeAllocations = useMemo(
        () =>
            selectedAllocations?.filter((alloc) =>
                isPrivateOffice(alloc as any),
            ),
        [selectedAllocations],
    );
    const selectedBoardroomAllocations = useMemo(
        () => selectedAllocations?.filter((alloc) => isBoardroom(alloc as any)),
        [selectedAllocations],
    );

    const allocationToDTInterval = useCallback(
        (alloc: ReservationSpace): DateTimeInterval => [
            toDateTime(alloc.startDate, alloc.checkinTime),
            toDateTime(alloc.endDate, alloc.checkoutTime),
        ],
        [],
    );

    const nonBusinessIntervals = useMemo(
        () =>
            showAllTogether
                ? []
                : buildNonBusinessIntervals(
                      windowStartDT,
                      windowEndDT,
                      experienceCheckinTime,
                      experienceCheckoutTime,
                  ),
        [
            showAllTogether,
            windowStartDT,
            windowEndDT,
            experienceCheckinTime,
            experienceCheckoutTime,
        ],
    );

    const coveredIntervalsGlobal: DateTimeInterval[] = useMemo(
        () =>
            mergeIntervals([
                ...selectedAllocations.map(allocationToDTInterval),
                ...nonBusinessIntervals,
            ]),
        [selectedAllocations, allocationToDTInterval, nonBusinessIntervals],
    );

    const remainingGapsGlobal = useMemo(
        () =>
            computeRemainingGaps(
                windowStartDT,
                windowEndDT,
                coveredIntervalsGlobal,
            ),
        [windowStartDT, windowEndDT, coveredIntervalsGlobal],
    );

    const coveredIntervalsOffices: DateTimeInterval[] = useMemo(
        () =>
            mergeIntervals([
                ...selectedOfficeAllocations.map(allocationToDTInterval),
                ...nonBusinessIntervals,
            ]),
        [
            selectedOfficeAllocations,
            allocationToDTInterval,
            nonBusinessIntervals,
        ],
    );

    const coveredIntervalsBoardrooms: DateTimeInterval[] = useMemo(
        () =>
            mergeIntervals([
                ...selectedBoardroomAllocations.map(allocationToDTInterval),
                ...nonBusinessIntervals,
            ]),
        [
            selectedBoardroomAllocations,
            allocationToDTInterval,
            nonBusinessIntervals,
        ],
    );

    const remainingGapsOffices = useMemo(
        () =>
            computeRemainingGaps(
                windowStartDT,
                windowEndDT,
                coveredIntervalsOffices,
            ),
        [windowStartDT, windowEndDT, coveredIntervalsOffices],
    );

    const remainingGapsBoardrooms = useMemo(
        () =>
            computeRemainingGaps(
                windowStartDT,
                windowEndDT,
                coveredIntervalsBoardrooms,
            ),
        [windowStartDT, windowEndDT, coveredIntervalsBoardrooms],
    );

    const isSpaceSelected = useCallback(
        (spaceId: number) =>
            selectedAllocations.some((allocation) => allocation.id === spaceId),
        [selectedAllocations],
    );

    const removeAllSegmentsForSpace = useCallback(
        (spaceId: number) => {
            const next = selectedAllocations.filter(
                (allocation) => allocation.id !== spaceId,
            );
            setValue(name, next, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
            });
        },
        [selectedAllocations, name, setValue],
    );

    const addSegmentsIntoGaps = useCallback(
        (space: Space, targetGaps: DateTimeInterval[]) => {
            if (!targetGaps?.length) return;
            const availableIntervals = getSpaceAvailableIntervals(
                space,
                windowStartDT,
                windowEndDT,
            );
            const segmentsToAdd = intersectIntervalLists(
                availableIntervals,
                targetGaps,
            );
            if (!segmentsToAdd?.length) return;

            const newEntries: ReservationSpace[] = segmentsToAdd.map(
                ([segmentStart, segmentEnd]) => {
                    const start = splitDateTime(segmentStart);
                    const end = splitDateTime(segmentEnd);
                    return {
                        id: space.id,
                        startDate: start.date,
                        endDate: end.date,
                        checkinTime: start.time,
                        checkoutTime: end.time,
                        name: space.name,
                        assignedName: space.assignedName,
                        spaceType: space.spaceType,
                        attendees: [],
                    };
                },
            );

            setValue(name, [...selectedAllocations, ...newEntries], {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
            });
        },
        [
            bookingStartDate,
            bookingEndDate,
            bookingCheckinTime,
            bookingCheckoutTime,
            selectedAllocations,
            name,
            setValue,
        ],
    );

    const addFullWindowSegment = useCallback(
        (space: Space) => {
            const fullWindowEntry: ReservationSpace = {
                id: space.id,
                startDate: bookingStartDate,
                endDate: bookingEndDate,
                checkinTime: bookingCheckinTime,
                checkoutTime: bookingCheckoutTime,
                name: space.name,
                assignedName: space.assignedName,
                spaceType: space.spaceType,
                attendees: [],
            };
            setValue(name, [...selectedAllocations, fullWindowEntry], {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
            });
        },
        [
            bookingStartDate,
            bookingEndDate,
            selectedAllocations,
            name,
            setValue,
            bookingCheckinTime,
            bookingCheckoutTime,
        ],
    );

    const handleToggleSpace = useCallback(
        (space: Space, hasAttendees = false) => {
            if (isSpaceSelected(space.id)) {
                if (showAllTogether && (isFreeBedroom(space) || hasAttendees))
                    return;
                removeAllSegmentsForSpace(space.id);
                return;
            }
            if (
                (showAllTogether && !isFreeBedroom(space)) ||
                (isFreeBedroom(space) && hasAttendees)
            ) {
                addFullWindowSegment(space);
                return;
            }
            if (isDualTrack) {
                if (isPrivateOffice(space)) {
                    addSegmentsIntoGaps(space, remainingGapsOffices);
                    return;
                }
                if (isBoardroom(space)) {
                    addSegmentsIntoGaps(space, remainingGapsBoardrooms);
                    return;
                }
                return;
            }
            if (showPrivateOfficeOnly) {
                addSegmentsIntoGaps(space, remainingGapsGlobal);
                return;
            }
            if (isFreeBedroom(space) && !hasAttendees) return;
            addSegmentsIntoGaps(space, remainingGapsGlobal);
        },
        [
            isSpaceSelected,
            removeAllSegmentsForSpace,
            showAllTogether,
            addFullWindowSegment,
            isDualTrack,
            addSegmentsIntoGaps,
            remainingGapsOffices,
            remainingGapsBoardrooms,
            showPrivateOfficeOnly,
            remainingGapsGlobal,
        ],
    );

    const sectionsToRender: Array<{
        title: string;
        subtitle: string;
        list: Space[];
    }> = useMemo(() => {
        if (showAllTogether) {
            return [
                {
                    title: hasByZoneCleaningFee
                        ? 'Select the spaces you require.'
                        : 'Room allocation',
                    subtitle: hasByZoneCleaningFee
                        ? 'Choose the spaces you require for this reservation and allocate rooms to your guests.'
                        : 'Allocate rooms to your guests. Select from attendees or type a placeholder.',
                    list: availableSpaces,
                },
            ];
        }

        if (isDualTrack) {
            return [
                {
                    title: 'Select your office.',
                    subtitle: 'Choose an office space for this reservation.',
                    list: privateOfficeSpaces,
                },
                {
                    title: 'Select your meeting room.',
                    subtitle: 'Choose a meeting room for the selected dates.',
                    list: boardroomSpaces,
                },
            ];
        }

        const sections: Array<{
            title: string;
            subtitle: string;
            list: Space[];
        }> = [];
        if (showPrivateOfficeOnly)
            sections.push({
                title: 'Select your office.',
                subtitle: 'Choose an office space for this reservation.',
                list: privateOfficeSpaces,
            });
        if (showBoardroomOnly || isMeetingRoom)
            sections.push({
                title: 'Select your meeting room.',
                subtitle: 'Choose a meeting room for the selected dates.',
                list: boardroomSpaces,
            });
        if (!showPrivateOfficeOnly && !showBoardroomOnly && !isMeetingRoom) {
            if (privateOfficeSpaces?.length)
                sections.push({
                    title: 'Private Offices',
                    subtitle: 'Choose an office space for this reservation.',
                    list: privateOfficeSpaces,
                });
            if (boardroomSpaces?.length)
                sections.push({
                    title: 'Meeting Rooms',
                    subtitle: 'Choose a meeting room for the selected dates.',
                    list: boardroomSpaces,
                });
            if (otherSpaces?.length)
                sections.push({
                    title: 'Other Spaces',
                    subtitle:
                        'Choose the spaces you require for this reservation.',
                    list: otherSpaces,
                });
        }
        return sections;
    }, [
        showAllTogether,
        isDualTrack,
        privateOfficeSpaces,
        boardroomSpaces,
        otherSpaces,
        showPrivateOfficeOnly,
        showBoardroomOnly,
        isMeetingRoom,
        availableSpaces,
    ]);

    useEffect(() => {
        if (spaceType === 'privateOffice' && !isMeetingRoom) {
            setValue(
                'spaces',
                selectedSpaces.filter(
                    (space: any) => getSpaceType(space) === 'privateOffice',
                ),
            );
        }
        if (spaceType === 'meetingRoom') {
            setValue(
                'spaces',
                selectedSpaces.filter(
                    (space: any) => getSpaceType(space) === 'boardroom',
                ),
            );
        }
    }, [spaceType, isMeetingRoom]);

    const officeAllocationSegments = useMemo(
        () => allocationsToSegments(selectedOfficeAllocations),
        [selectedOfficeAllocations],
    );
    const boardroomAllocationSegments = useMemo(
        () => allocationsToSegments(selectedBoardroomAllocations),
        [selectedBoardroomAllocations],
    );

    const allocationGroups = useMemo(
        () =>
            buildAllocationGroups(
                windowStartDT,
                windowEndDT,
                officeAllocationSegments,
                boardroomAllocationSegments,
                nonBusinessIntervals,
            ),
        [
            windowStartDT,
            windowEndDT,
            officeAllocationSegments,
            boardroomAllocationSegments,
            nonBusinessIntervals,
        ],
    );

    const hasIncomplete = useMemo(
        () =>
            allocationGroups.some((group) => {
                if (
                    (isMeetingRoom || spaceType === 'meetingRoom') &&
                    !group.boardroomId
                ) {
                    return true;
                }
                if (spaceType === 'privateOffice' && !group.officeId) {
                    return true;
                }
                return false;
            }),
        [allocationGroups, isMeetingRoom, spaceType],
    );

    const prevIncompleteRef = useRef<boolean | undefined>(undefined);
    useEffect(() => {
        if (
            prevIncompleteRef.current !== undefined &&
            prevIncompleteRef.current === hasIncomplete
        ) {
            return;
        }

        prevIncompleteRef.current = hasIncomplete;
        if (hasIncomplete) {
            handleError('spaces', 'Incomplete room selection');
        } else {
            clearError('spaces');
        }
    }, [hasIncomplete]);

    const showAllocations =
        spaceType === 'privateOffice' ||
        spaceType === 'meetingRoom' ||
        isMeetingRoom;

    useEffect(() => {
        if (
            (spaceType === 'privateOffice' || isDualTrack) &&
            privateOfficeSpaces?.length === 1 &&
            selectedOfficeAllocations.length === 0
        ) {
            const onlyOneOffice = privateOfficeSpaces[0];
            if (!isSpaceSelected(onlyOneOffice.id)) {
                handleToggleSpace(onlyOneOffice);
            }
        }
    }, [
        spaceType,
        isDualTrack,
        privateOfficeSpaces,
        selectedOfficeAllocations,
        isSpaceSelected,
        handleToggleSpace,
    ]);

    useEffect(() => {
        if (
            (isMeetingRoom || spaceType === 'meetingRoom') &&
            boardroomSpaces?.length === 1 &&
            selectedBoardroomAllocations.length === 0
        ) {
            const onlyOneBoardroom = boardroomSpaces[0];
            if (!isSpaceSelected(onlyOneBoardroom.id)) {
                handleToggleSpace(onlyOneBoardroom);
            }
        }
    }, [
        isMeetingRoom,
        spaceType,
        boardroomSpaces,
        selectedBoardroomAllocations,
        isSpaceSelected,
        handleToggleSpace,
    ]);

    const accordionHeading = useMemo(() => {
        if (sectionsToRender.length === 1) {
            return {
                title: sectionsToRender[0].title,
                subtitle: sectionsToRender[0].subtitle,
            };
        }
        return {
            title: 'Room allocation',
            subtitle: hasByZoneCleaningFee
                ? 'Choose the spaces you require for this reservation and allocate rooms to your guests.'
                : 'Allocate rooms to your guests. Select from attendees or type a placeholder.',
        };
    }, [sectionsToRender, hasByZoneCleaningFee]);

    const roomAllocationBody = (
        <>
            {sectionsToRender.length === 0 ? (
                <Typography variant='body3' sx={{ fontStyle: 'italic' }}>
                    No spaces to display.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {sectionsToRender.map((section) => (
                        <Box key={section.title}>
                            <Grid container spacing={2}>
                                {section.list.map((space) => {
                                    const checked = isSpaceSelected(space.id);

                                    const availableIntervals =
                                        getSpaceAvailableIntervals(
                                            space,
                                            windowStartDT,
                                            windowEndDT,
                                        );

                                    const mergedAvailable =
                                        mergeIntervals(availableIntervals);
                                    const fullyCoversWindow =
                                        mergedAvailable.length === 1 &&
                                        mergedAvailable[0][0] ===
                                            windowStartDT &&
                                        mergedAvailable[0][1] === windowEndDT;

                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                            key={space.id}
                                        >
                                            <SpaceCardContent
                                                space={space}
                                                isSelected={checked}
                                                experienceType={experienceType}
                                                handleCardClick={() => {
                                                    handleToggleSpace(space);
                                                }}
                                                handleAddSpaceForAttendees={() => {
                                                    handleToggleSpace(
                                                        space,
                                                        true,
                                                    );
                                                }}
                                                assignedAttendeesCount={
                                                    formSpaces.find(
                                                        (s) =>
                                                            s.id === space.id,
                                                    )?.attendees?.length ?? 0
                                                }
                                                shouldShowSelected={
                                                    hasByZoneCleaningFee ||
                                                    experienceType ===
                                                        'commercial'
                                                }
                                                isCommercialSpace={
                                                    isPrivateOffice(space) ||
                                                    isBoardroom(space)
                                                }
                                                isReservationForm={true}
                                                isExperienceForm={false}
                                                startDate={bookingStartDate}
                                                endDate={bookingEndDate}
                                                partiallyAvailable={
                                                    !fullyCoversWindow
                                                }
                                                disabled={(() => {
                                                    if (checked) return false;
                                                    if (showAllTogether)
                                                        return false;

                                                    let targetGaps =
                                                        remainingGapsGlobal;

                                                    if (isDualTrack) {
                                                        if (
                                                            isPrivateOffice(
                                                                space,
                                                            )
                                                        ) {
                                                            targetGaps =
                                                                remainingGapsOffices;
                                                        } else if (
                                                            isBoardroom(space)
                                                        ) {
                                                            targetGaps =
                                                                remainingGapsBoardrooms;
                                                        }
                                                    } else if (
                                                        showPrivateOfficeOnly
                                                    ) {
                                                        targetGaps =
                                                            remainingGapsGlobal;
                                                    }

                                                    const intervals =
                                                        getSpaceAvailableIntervals(
                                                            space,
                                                            windowStartDT,
                                                            windowEndDT,
                                                        );

                                                    const canAdd =
                                                        intersectIntervalLists(
                                                            intervals,
                                                            targetGaps,
                                                        ).length > 0;

                                                    return !canAdd;
                                                })()}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    ))}
                </Stack>
            )}

            {selectedSpaces && showAllocations && (
                <Box
                    sx={{
                        mt: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'custom.lightBorderColor',
                        overflow: 'hidden',
                        backgroundColor: 'background.paper',
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            backgroundColor: (theme) =>
                                theme.palette.action.hover,
                            borderBottom: '1px solid',
                            borderColor: 'custom.lightBorderColor',
                        }}
                    >
                        <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                            flexWrap='wrap'
                        >
                            <CheckCircle
                                sx={{
                                    mr: 1,
                                    fontSize: { xs: 18, sm: 20 },
                                    color: theme.palette.success.main,
                                }}
                            />
                            <Typography
                                variant='h6'
                                sx={{
                                    fontWeight: 800,
                                }}
                            >
                                Selected room allocation
                            </Typography>
                        </Stack>
                    </Box>

                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Stack spacing={1.75}>
                            {allocationGroups.map((group, idx) => (
                                <Box key={`${group.startDT}-${group.endDT}`}>
                                    <Typography
                                        variant='overline'
                                        sx={{
                                            fontWeight: 800,
                                            letterSpacing: 0.2,
                                        }}
                                    >
                                        {formatGroupRange(group)}
                                    </Typography>

                                    {spaceType === 'privateOffice' &&
                                        (group.officeId ? (
                                            <>
                                                <Typography variant='body3'>
                                                    Private Office
                                                </Typography>
                                                <Typography
                                                    variant='body3'
                                                    sx={{
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {
                                                        spaces?.find(
                                                            (space: any) =>
                                                                space.id ===
                                                                group.officeId,
                                                        )?.assignedName
                                                    }
                                                </Typography>
                                            </>
                                        ) : (
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    color: 'error.main',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Error fontSize='small' />{' '}
                                                Please select{' '}
                                                {selectedSpaces.filter(
                                                    (space: any) =>
                                                        space.spaceType ===
                                                        'privateOffice',
                                                ).length > 0
                                                    ? 'an additional '
                                                    : 'an '}
                                                office.
                                            </Typography>
                                        ))}

                                    {(isMeetingRoom ||
                                        spaceType === 'meetingRoom') &&
                                        (group.boardroomId ? (
                                            <>
                                                <Typography
                                                    variant='body3'
                                                    sx={{
                                                        mt:
                                                            spaceType ===
                                                            'privateOffice'
                                                                ? 0.75
                                                                : 0,
                                                    }}
                                                >
                                                    Meeting Room
                                                </Typography>
                                                <Typography
                                                    variant='body3'
                                                    sx={{
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {
                                                        spaces?.find(
                                                            (
                                                                space,
                                                            ): space is any =>
                                                                'id' in space &&
                                                                space.id ===
                                                                    group.boardroomId,
                                                        )?.assignedName
                                                    }
                                                </Typography>
                                            </>
                                        ) : (
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    color: 'error.main',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,

                                                    mt:
                                                        spaceType ===
                                                        'privateOffice'
                                                            ? 0.75
                                                            : 0,
                                                }}
                                            >
                                                <Error fontSize='small' />{' '}
                                                Please select{' '}
                                                {selectedSpaces.filter(
                                                    (space: any) =>
                                                        space.spaceType ===
                                                        'meetingRoom',
                                                ).length > 0
                                                    ? 'an additional '
                                                    : 'a '}
                                                meeting room.
                                            </Typography>
                                        ))}

                                    {idx < allocationGroups.length - 1 && (
                                        <Divider sx={{ mt: 2 }} />
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Box>
            )}
        </>
    );

    if (collapsible) {
        return (
            <Accordion
                expanded={roomAllocationExpanded}
                onChange={(_, next) => setRoomAllocationExpanded(next)}
                disableGutters
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'custom.lightGrey',
                    backgroundColor: 'background.paper',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary
                    expandIcon={
                        <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
                    }
                    sx={{
                        px: 2,
                        py: 1.25,
                        minHeight: 56,
                        '& .MuiAccordionSummary-content': {
                            my: 0.5,
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 0.5,
                        },
                    }}
                >
                    <Stack spacing={0.5}>
                        <Stack
                            direction='row'
                            spacing={0.75}
                            alignItems='center'
                        >
                            <Typography
                                variant='body3'
                                sx={{
                                    fontWeight: 800,
                                    color: 'text.secondary',
                                    ...(showCommercialSpaceRequiredError && {
                                        color: 'error.main',
                                    }),
                                }}
                            >
                                {accordionHeading.title}
                            </Typography>
                            {showAllTogether && hasByZoneCleaningFee && (
                                <CustomTooltip title='These spaces have a separate cleaning fee which will be added to the total'>
                                    <InfoOutlined
                                        fontSize='small'
                                        color='action'
                                    />
                                </CustomTooltip>
                            )}
                        </Stack>

                        <Typography
                            variant='body3'
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.35,
                                fontWeight: 400,
                            }}
                        >
                            {accordionHeading.subtitle}
                        </Typography>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                    {roomAllocationBody}
                </AccordionDetails>
            </Accordion>
        );
    }

    return <Box>{roomAllocationBody}</Box>;
};

export default ReservationSpaces;
